// The MCP connection: an external assistant — Claude Code, Claude Desktop —
// reaching the app as the operator who connected it. Protocol
// vocabulary (Client, Token, Scope) keeps its English form; what a person
// does here — verbinden, erlauben, trennen — is German.

import { TERMS } from './terms'

export const MCP = {
  consentTitle: (client: string) => `${client} mit ${TERMS.appName} verbinden`,
  consentIntro:
    'Der Assistent greift danach mit deinem Konto zu und darf genau das, was du selbst darfst — nicht mehr.',
  scopeLabels: {
    'mcp:read': 'Lesen',
    'mcp:write': 'Ändern',
  } as Record<string, string>,
  scopeHints: {
    'mcp:read': 'Alles ansehen, was du in der Anwendung sehen darfst.',
    'mcp:write':
      'Anlegen, ändern und löschen, was du selbst darfst. Jede Änderung steht mit deinem Namen in der Historie.',
  } as Record<string, string>,
  notAsked: 'Nicht angefragt',
  allow: 'Erlauben',
  deny: 'Abbrechen',
  denied: 'Du hast die Verbindung abgebrochen. Das Fenster kann zu.',
  consentRefused:
    'Diese Anfrage gilt nicht mehr. Starte die Verbindung im Assistenten neu.',
  /** What granting answers with where a customer tries it. Customers have an
   *  account, and this is not what it is for. */
  staffOnly:
    'Dieses Konto darf keinen Assistenten verbinden. Melde dich mit einem internen Konto an.',

  /** The page an assistant prepares for the one step it cannot take itself. */
  upload: {
    title: 'Bild hinzufügen',
    intro:
      'Dein Assistent hat diese Seite vorbereitet. Zieh das Bild hier hinein — Titel, Tags und die Zuordnung sind schon gesetzt.',
    forLabel: 'Gehört zu',
    titleLabel: 'Titel',
    tagsLabel: 'Tags',
    noTitle: 'Kein Titel',
    noTags: 'Keine Tags',
    drop: 'Bild hierher ziehen oder auswählen',
    formats: (mb: number) => `JPEG, PNG, WebP oder AVIF, bis ${mb} MB`,
    uploading: 'Wird hochgeladen …',
    failed: 'Das Bild konnte nicht hochgeladen werden.',
    done: 'Fertig. Sag deinem Assistenten Bescheid, er macht weiter.',
    duplicate:
      'Dieses Bild war schon in der Mediathek. Es wurde trotzdem angelegt und zugeordnet.',
    linkInvalid:
      'Dieser Link gilt nicht mehr — er wurde schon benutzt, ist abgelaufen, oder gehört zu einem anderen Konto. Lass dir vom Assistenten einen neuen geben.',
  },

  /** The address an assistant is pointed at. */
  server: {
    serverMissing:
      'Der Server ist aus: ohne API_URL gibt es nichts, woran Tokens gebunden werden könnten.',
    title: 'Server URL',
    loadFailed: 'Die Server URL konnte nicht geladen werden.',
    copy: 'URL kopieren',
    copied: 'URL kopiert',
  },

  /** The assistants currently reaching the app — the System page lists them,
   *  the admin dashboard shows the same list. */
  connections: {
    title: 'Verbundene Assistenten',
    subtitle: 'Aktive Zugänge über MCP',
    empty: 'Noch kein Assistent verbunden.',
    loadFailed: 'Die Verbindungen konnten nicht geladen werden.',
    revoke: 'Verbindung trennen',
    revokeMessage: (client: string, user: string) =>
      `${client} verliert damit den Zugang mit dem Konto von ${user}. Der Assistent muss neu verbunden werden, um wieder zu arbeiten.`,
  },
} as const

/** A scope as a person reads it. The table is keyed loosely, so an unknown
 *  scope falls back to its own name rather than to nothing. */
export const scopeLabel = (scope: string): string =>
  MCP.scopeLabels[scope] ?? scope
